import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api';

type AuditItem = {
  id: string;
  created_at: string;
  event: string;
  actor: string;
  session_id: string | null;
  metadata: any;
};

const EVENT_OPTIONS = ['', 'created', 'submitted', 'synthesis_started', 'synthesis_completed', 'synthesis_failed', 'synthesis_edited', 'synthesis_regenerated', 'approved', 'delivered', 'rejected', 'delivery_failed', 'email_pending', 'email_deferred', 'admin_settings_updated'];

export default function Audit() {
  const [search, setSearch] = useSearchParams();
  const event = search.get('event') || '';
  const actor = search.get('actor') || '';
  const sessionFilter = search.get('session_id') || '';

  const [items, setItems] = useState<AuditItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setItems(null); setError(null);
    (async () => {
      try {
        const r = await api.audit({ event: event || undefined, actor: actor || undefined, session_id: sessionFilter || undefined });
        if (!cancelled) setItems(r.items);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Could not load.');
      }
    })();
    return () => { cancelled = true; };
  }, [event, actor, sessionFilter]);

  function update(key: string, value: string) {
    const next = new URLSearchParams(search);
    if (value) next.set(key, value); else next.delete(key);
    setSearch(next, { replace: true });
  }

  return (
    <div className="max-w-6xl">
      <div className="flex items-baseline justify-between mb-6">
        <h1 className="font-display text-3xl font-medium text-ink">Audit</h1>
        {items && <span className="font-mono text-[11px] uppercase tracking-widest text-ink-light">{items.length} event{items.length === 1 ? '' : 's'}</span>}
      </div>

      <div className="flex flex-wrap gap-3 mb-6 pb-4 border-b border-rule">
        <label className="flex flex-col">
          <span className="font-mono text-[10px] uppercase tracking-widest text-ink-light mb-1">Event</span>
          <select value={event} onChange={(e) => update('event', e.target.value)} className="bg-paper border border-rule px-3 py-1.5 font-serif text-sm text-ink focus:outline-none focus:border-accent min-w-[200px]">
            {EVENT_OPTIONS.map((e) => <option key={e || 'all'} value={e}>{e || 'All events'}</option>)}
          </select>
        </label>
        <label className="flex flex-col">
          <span className="font-mono text-[10px] uppercase tracking-widest text-ink-light mb-1">Actor</span>
          <input
            type="text"
            value={actor}
            onChange={(e) => update('actor', e.target.value)}
            placeholder="operator | system | email"
            className="bg-paper border border-rule px-3 py-1.5 font-serif text-sm text-ink focus:outline-none focus:border-accent w-56"
          />
        </label>
        <label className="flex flex-col">
          <span className="font-mono text-[10px] uppercase tracking-widest text-ink-light mb-1">Session ID</span>
          <input
            type="text"
            value={sessionFilter}
            onChange={(e) => update('session_id', e.target.value)}
            placeholder="paste a UUID"
            className="bg-paper border border-rule px-3 py-1.5 font-mono text-xs text-ink focus:outline-none focus:border-accent w-72"
          />
        </label>
      </div>

      {error && <p className="font-serif text-accent">{error}</p>}
      {!items && !error && <p className="font-mono text-[11px] uppercase tracking-wider text-ink-light">Loading…</p>}
      {items && items.length === 0 && <p className="font-serif text-ink-light italic">No events match these filters.</p>}
      {items && items.length > 0 && (
        <div className="overflow-x-auto border border-rule bg-paper">
          <table className="w-full font-mono text-[12px]">
            <thead>
              <tr className="bg-cream-dark text-left">
                <Th>Time</Th>
                <Th>Event</Th>
                <Th>Actor</Th>
                <Th>Session</Th>
                <Th>Metadata</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-t border-rule align-top">
                  <td className="px-3 py-2 text-ink-light whitespace-nowrap" title={item.created_at}>
                    {new Date(item.created_at).toISOString().replace('T', ' ').slice(0, 19)}
                  </td>
                  <td className="px-3 py-2 text-ink">
                    <span className={eventBadgeClass(item.event)}>{item.event}</span>
                  </td>
                  <td className="px-3 py-2 text-ink-soft">{item.actor}</td>
                  <td className="px-3 py-2">
                    {item.session_id ? (
                      <Link to={`/review/${item.session_id}`} className="text-accent hover:underline" title={item.session_id}>
                        {item.session_id.slice(0, 8)}…
                      </Link>
                    ) : <span className="text-ink-light">—</span>}
                  </td>
                  <td className="px-3 py-2 text-ink-soft text-[11px] max-w-md break-words">
                    {item.metadata && Object.keys(item.metadata).length > 0
                      ? <code>{JSON.stringify(item.metadata)}</code>
                      : <span className="text-ink-light">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Th({ children }: { children?: React.ReactNode }) {
  return <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-ink-light">{children}</th>;
}

function eventBadgeClass(e: string): string {
  const failure = ['synthesis_failed', 'delivery_failed', 'rejected'].includes(e);
  const success = ['delivered', 'approved', 'synthesis_completed'].includes(e);
  if (failure) return 'inline-block px-2 py-0.5 bg-red-50 text-red-800 text-[11px]';
  if (success) return 'inline-block px-2 py-0.5 bg-emerald-50 text-emerald-800 text-[11px]';
  return 'inline-block px-2 py-0.5 bg-cream-dark text-ink-soft text-[11px]';
}
